import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ListActionBar from 'src/components/ListActionBar';
import TableContainer from 'src/components/Table/TableContainer';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import TableHeadTypography from 'src/components/TableHeadTypography';
import Iconify from 'src/components/Iconify';
import useTypographyMediaQuery from 'src/hooks/useTypographyMediaQuery';
import { useInfinitePackagedAppImpactedDevices } from 'src/hooks/packagedApp';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { DEVICES_PREVIOUS_PATH } from 'src/pages/devices/deviceDetails/constant';
import { formatDate } from 'src/utils/formatTime';
import { getWdacStatusLabel } from 'src/utils/enums/wdacStatus';

const PAGE_LIMIT = 50;
const DEFAULT_SORT = { column: 'lastEventSystemTime', direction: 'desc' };

const IMPACTED_DEVICE_COLUMNS = [
  {
    key: 'name',
    label: 'Device Name',
    sortable: true,
    sortKey: 'name',
    width: '34%',
  },
  {
    key: 'enforcementMode',
    label: 'Enforcement Mode',
    sortable: false,
    width: '33%',
  },
  {
    key: 'lastEventSystemTime',
    label: 'Last Event System Time',
    sortable: true,
    sortKey: 'lastEventSystemTime',
    width: '33%',
  },
];

const ImpactedDevicesTab = () => {
  const { packagedAppId } = useParams();
  const navigate = useNavigate();
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();

  const [searchValue, setSearchValue] = useState('');
  const [sort, setSort] = useState(DEFAULT_SORT);
  const trimmedSearchValue = useMemo(() => searchValue.trim(), [searchValue]);

  const apiQuery = useMemo(
    () => ({
      ...(trimmedSearchValue ? { search: trimmedSearchValue } : {}),
      sortColumn: sort.column,
      sortDesc: sort.direction === 'desc',
    }),
    [sort, trimmedSearchValue],
  );

  const devicesQuery = useInfinitePackagedAppImpactedDevices({
    packagedAppId,
    apiQuery,
    limit: PAGE_LIMIT,
  });

  const devices = useMemo(
    () => devicesQuery.data?.pages?.flatMap(page => page.affectedDevices || []) || [],
    [devicesQuery.data],
  );
  const totalCount = devicesQuery.data?.pages?.[0]?.totalCount ?? 0;

  const loadMore = useCallback(() => {
    if (devicesQuery.hasNextPage) {
      devicesQuery.fetchNextPage();
    }
  }, [devicesQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: devicesQuery.isFetchingNextPage,
    hasNextPage: devicesQuery.hasNextPage,
    disabled: false,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const handleSort = useCallback(column => {
    if (!column.sortable || !column.sortKey) {
      return;
    }

    setSort(prev =>
      prev.column === column.sortKey
        ? { column: column.sortKey, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { column: column.sortKey, direction: 'desc' },
    );
  }, []);

  const handleRowClick = useCallback(
    deviceId => {
      sessionStorage.setItem(
        DEVICES_PREVIOUS_PATH,
        `${window.location.pathname}${window.location.search}`,
      );
      navigate(`${PORTAL_ROUTE}/devices/${deviceId}/general`);
    },
    [navigate],
  );

  const renderCell = useCallback((column, device) => {
    const value = device[column.key];

    return (
      <TableCell key={`${device.id}-${column.key}`} sx={{ width: column.width, py: 0.5, px: 2 }}>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', wordBreak: 'break-word' }}>
          {column.key === 'lastEventSystemTime'
            ? formatDate(value) || '--'
            : column.key === 'enforcementMode'
              ? getWdacStatusLabel(value)
              : value || '--'}
        </Typography>
      </TableCell>
    );
  }, []);

  const ImpactedDeviceColumnHeader = ({ column }) => (
    <Stack
      direction="row"
      gap={0.5}
      alignItems="center"
      onClick={column.sortable ? () => handleSort(column) : undefined}
      sx={{ cursor: column.sortable ? 'pointer' : 'default' }}>
      <TableHeadTypography
        hasSorting={sort.column === column.sortKey}
        mediumScreen={mediumScreen}
        largeScreen={largeScreen}
        smallScreen={smallScreen}>
        {column.label}
      </TableHeadTypography>
      {column.sortable ? (
        <Iconify
          icon={
            sort.column === column.sortKey
              ? sort.direction === 'asc'
                ? 'mdi:arrow-up'
                : 'mdi:arrow-down'
              : 'mdi:swap-vertical'
          }
          width={14}
          height={14}
          color={sort.column === column.sortKey ? '#7DFF92' : '#9CA3AF'}
        />
      ) : null}
    </Stack>
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <ListActionBar
        onSearch={setSearchValue}
        defaultSearch={searchValue}
        onResetSearch={() => setSearchValue('')}
        searchPlaceholder="Search devices"
        filterButtonCount={totalCount}
      />

      <TableContainer
        roundedTop
        styles={{
          maxHeight: 'calc(100vh - 368px)',
        }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': { fontSize: '0.7rem' },
          }}>
          <colgroup>
            {IMPACTED_DEVICE_COLUMNS.map(column => (
              <col key={column.key} style={{ width: column.width }} />
            ))}
          </colgroup>
          <TableHead sx={{ backgroundColor: '#3F3F3F' }}>
            <TableRow>
              {IMPACTED_DEVICE_COLUMNS.map(column => (
                <TableCell key={column.key} sx={{ p: '8px 16px', verticalAlign: 'middle' }}>
                  <ImpactedDeviceColumnHeader column={column} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
            {devicesQuery.isLoading ? (
              <LoadingRows columnCount={IMPACTED_DEVICE_COLUMNS.length} rowCount={6} />
            ) : (
              <>
                {devices.map((device, index) => (
                  <TableRow
                    key={device.id}
                    ref={index === devices.length - 1 ? sentryRef : null}
                    hover
                    onClick={() => handleRowClick(device.id)}
                    sx={{ '& td': { py: 0.35 }, cursor: 'pointer' }}>
                    {IMPACTED_DEVICE_COLUMNS.map(column => renderCell(column, device))}
                  </TableRow>
                ))}
                {devicesQuery.isFetchingNextPage ? (
                  <LoadingRows columnCount={IMPACTED_DEVICE_COLUMNS.length} rowCount={3} />
                ) : null}
                {devices.length === 0 ? (
                  <NoDataCell colSpan={IMPACTED_DEVICE_COLUMNS.length}>No Data</NoDataCell>
                ) : null}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ImpactedDevicesTab;
